import { Link } from 'react-router-dom';
import { useState } from 'react';
import { 
  ArrowRight, CheckCircle, Clock, Users, Award, BookOpen, 
  Wrench, Cog, Building2, Briefcase, GraduationCap, Play,
  Monitor, FileText, Settings, Globe, Shield, TrendingUp, Layers
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { createLead } from '@/lib/api';

// Infor CloudSuite Certification Training Paths
const trainingPaths = [
  {
    id: 'cloudsuite-industrial',
    name: 'Infor CloudSuite Industrial (SyteLine)',
    level: 'Beginner to Advanced',
    duration: '10-12 weeks',
    description: 'Master Infor CloudSuite Industrial (formerly SyteLine) for discrete manufacturing including production planning, shop floor control, and supply chain management.',
    color: 'from-yellow-500 to-yellow-700',
    icon: '🏭',
    topics: [
      'CloudSuite Industrial Overview',
      'Production Planning & Scheduling',
      'Shop Floor Control',
      'Material Requirements Planning (MRP)',
      'Inventory & Warehouse Management',
      'Quality Management',
      'Financial Management',
      'Advanced Planning & Scheduling'
    ],
    outcomes: [
      'Configure CloudSuite Industrial modules',
      'Implement manufacturing workflows',
      'Set up MRP and production planning',
      'Manage quality control processes',
      'Prepare for Infor certification'
    ],
    salary: '$80,000 - $125,000',
    demand: 'High',
    industries: ['Discrete Manufacturing', 'Industrial Equipment', 'Automotive']
  },
  {
    id: 'cloudsuite-distribution',
    name: 'Infor CloudSuite Distribution',
    level: 'Beginner to Advanced',
    duration: '8-10 weeks',
    description: 'Become an expert in Infor CloudSuite Distribution for wholesale distributors covering order management, inventory, procurement, and warehouse operations.',
    color: 'from-green-500 to-green-700',
    icon: '📦',
    topics: [
      'Distribution Management Overview',
      'Order Management',
      'Inventory Replenishment',
      'Procurement & Purchasing',
      'Warehouse Management',
      'Pricing & Promotions',
      'Customer Management',
      'Financial Integration'
    ],
    outcomes: [
      'Configure distribution processes',
      'Implement order-to-cash workflows',
      'Optimize inventory management',
      'Set up warehouse operations',
      'Prepare for Infor certification'
    ],
    salary: '$75,000 - $115,000',
    demand: 'High',
    industries: ['Wholesale Distribution', 'Food & Beverage', 'Electronics']
  },
  {
    id: 'cloudsuite-financials',
    name: 'Infor CloudSuite Financials',
    level: 'Beginner to Advanced',
    duration: '10-12 weeks',
    description: 'Master Infor CloudSuite Financials & Supply Management for comprehensive financial operations, global financial management, and procurement.',
    color: 'from-blue-500 to-blue-700',
    icon: '💰',
    topics: [
      'General Ledger & Financial Reporting',
      'Accounts Payable',
      'Accounts Receivable',
      'Cash Management',
      'Fixed Assets',
      'Project Accounting',
      'Supply Management',
      'Global Finance Management'
    ],
    outcomes: [
      'Configure financial modules',
      'Implement multi-entity accounting',
      'Set up supply management',
      'Create financial reports',
      'Prepare for Infor certification'
    ],
    salary: '$85,000 - $130,000',
    demand: 'Very High',
    industries: ['All Industries', 'Healthcare', 'Public Sector']
  },
  {
    id: 'cloudsuite-hcm',
    name: 'Infor CloudSuite HCM',
    level: 'Intermediate to Advanced',
    duration: '8-10 weeks',
    description: 'Specialize in Infor CloudSuite Human Capital Management for talent management, workforce management, and HR service delivery.',
    color: 'from-purple-500 to-purple-700',
    icon: '👥',
    topics: [
      'Global HR Management',
      'Talent Acquisition',
      'Talent Management',
      'Workforce Management',
      'Payroll Management',
      'Learning Management',
      'HR Service Delivery',
      'People Analytics'
    ],
    outcomes: [
      'Configure HCM modules',
      'Implement talent processes',
      'Set up workforce management',
      'Create HR analytics',
      'Prepare for Infor certification'
    ],
    salary: '$80,000 - $120,000',
    demand: 'High',
    industries: ['Healthcare', 'Hospitality', 'Manufacturing']
  }
];

// Infor Technical Track (Add-on)
const technicalPath = {
  id: 'birst-analytics',
  name: 'Infor Birst Analytics',
  level: 'Intermediate',
  duration: '6-8 weeks',
  description: 'Master Infor Birst for enterprise analytics, data visualization, and business intelligence across CloudSuite applications.',
  color: 'from-orange-500 to-orange-700',
  icon: '📊',
  salary: '$90,000 - $135,000',
  demand: 'Very High'
};

// Infor Modules
const inforModules = [
  { id: 'financials', name: 'Financial Management', icon: TrendingUp, duration: '2.5 weeks', topics: ['General Ledger', 'AP/AR', 'Fixed Assets', 'Cash Management', 'Budgeting'] },
  { id: 'supply', name: 'Supply Management', icon: Globe, duration: '2 weeks', topics: ['Procurement', 'Strategic Sourcing', 'Supplier Management', 'Contract Management', 'Spend Analysis'] },
  { id: 'manufacturing', name: 'Manufacturing', icon: Cog, duration: '2.5 weeks', topics: ['Production Planning', 'Shop Floor', 'MRP', 'Quality Management', 'APS'] },
  { id: 'warehouse', name: 'Warehouse Management', icon: Building2, duration: '2 weeks', topics: ['Inventory Control', 'Warehouse Operations', 'RF/Mobile', 'Labor Management', 'Shipping'] },
  { id: 'hcm', name: 'Human Capital Management', icon: Users, duration: '2 weeks', topics: ['Core HR', 'Talent Management', 'Workforce Management', 'Payroll', 'Learning'] },
  { id: 'analytics', name: 'Birst Analytics', icon: Monitor, duration: '1.5 weeks', topics: ['Data Modeling', 'Visualizations', 'Dashboards', 'Reports', 'Embedded Analytics'] }
];

// Corporate clients
const corporateClients = ['Ferrari', 'Whole Foods', 'Hershey', 'Heineken', 'Triumph', 'Harley-Davidson'];

// Industries
const industries = [
  { name: 'Manufacturing', icon: Cog },
  { name: 'Distribution', icon: Globe },
  { name: 'Healthcare', icon: Shield },
  { name: 'Food & Beverage', icon: Building2 },
  { name: 'Automotive', icon: Settings },
  { name: 'Aerospace', icon: Layers }
];

export default function InforDetailPage() {
  const [selectedPath, setSelectedPath] = useState('cloudsuite-industrial');
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);

  const handleDemoRequest = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      await createLead({
        first_name: 'Infor Demo',
        last_name: 'Request',
        email,
        message: 'Interested in Infor CloudSuite Training - Demo Request',
        source: 'infor_demo'
      });
      toast.success('Demo request submitted! We\'ll contact you within 24 hours.');
      setEmail('');
    } catch (error) {
      toast.error('Failed to submit. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen pt-24" data-testid="infor-detail-page">
      {/* Hero Section */}
      <section className="py-16 lg:py-24 bg-gradient-to-br from-yellow-50 via-white to-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <Link to="/programs" className="inline-flex items-center text-sm text-yellow-600 hover:text-yellow-700 mb-6">
                <ArrowRight className="w-4 h-4 mr-1 rotate-180" />
                Back to Programs
              </Link>
              
              <Badge className="bg-yellow-100 text-yellow-700 mb-4">Industry-Specific Cloud ERP</Badge>
              
              <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-yellow-500 to-yellow-700 flex items-center justify-center text-3xl mb-6">
                <Layers className="w-8 h-8 text-white" />
              </div>
              
              <h1 className="text-4xl sm:text-5xl font-bold text-slate-900 mb-4">
                Infor CloudSuite Training
              </h1>
              <p className="text-xl text-yellow-600 font-medium mb-4">
                Industry-Specific Cloud ERP Solutions
              </p>
              <p className="text-lg text-slate-600 mb-8">
                Infor CloudSuite delivers industry-specific ERP solutions for manufacturing, distribution, 
                healthcare, and more. Master CloudSuite Industrial, Distribution, Financials, and HCM 
                to become a sought-after Infor consultant.
              </p>
              
              <div className="flex flex-wrap gap-4 mb-8">
                <Link to="/pricing">
                  <Button className="bg-yellow-600 hover:bg-yellow-700 text-white rounded-full px-6" data-testid="infor-enroll-btn">
                    Enroll Now <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
                <Link to="/webinar">
                  <Button variant="outline" className="rounded-full px-6 border-yellow-200 text-yellow-700 hover:bg-yellow-50">
                    <Play className="w-4 h-4 mr-2" /> Free Demo Session
                  </Button>
                </Link>
              </div>

              {/* Quick Stats */}
              <div className="grid grid-cols-3 gap-4">
                <div className="text-center p-3 bg-white rounded-lg shadow-sm">
                  <p className="text-2xl font-bold text-yellow-600">68K+</p>
                  <p className="text-xs text-slate-500">Customers</p>
                </div>
                <div className="text-center p-3 bg-white rounded-lg shadow-sm">
                  <p className="text-2xl font-bold text-yellow-600">$110K+</p>
                  <p className="text-xs text-slate-500">Avg Salary</p>
                </div>
                <div className="text-center p-3 bg-white rounded-lg shadow-sm">
                  <p className="text-2xl font-bold text-yellow-600">91%</p>
                  <p className="text-xs text-slate-500">Placement Rate</p>
                </div>
              </div>
            </div>
            
            {/* Program Highlights Card */}
            <Card className="shadow-xl border-0 bg-white">
              <CardHeader className="bg-gradient-to-r from-yellow-600 to-yellow-700 text-white rounded-t-lg">
                <CardTitle className="flex items-center">
                  <Award className="w-5 h-5 mr-2" />
                  Program Highlights
                </CardTitle>
              </CardHeader>
              <CardContent className="p-6 space-y-4">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-yellow-100 rounded-lg flex items-center justify-center">
                    <Clock className="w-5 h-5 text-yellow-700" />
                  </div>
                  <div>
                    <p className="font-medium text-slate-900">Flexible Duration</p>
                    <p className="text-sm text-slate-600">8-12 weeks based on track</p>
                  </div>
                </div>
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-yellow-100 rounded-lg flex items-center justify-center">
                    <Monitor className="w-5 h-5 text-yellow-700" />
                  </div>
                  <div>
                    <p className="font-medium text-slate-900">Live Online Training</p>
                    <p className="text-sm text-slate-600">Expert-led via Zoom/Teams</p>
                  </div>
                </div>
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-yellow-100 rounded-lg flex items-center justify-center">
                    <Layers className="w-5 h-5 text-yellow-700" />
                  </div>
                  <div>
                    <p className="font-medium text-slate-900">Hands-On Labs</p>
                    <p className="text-sm text-slate-600">Infor CloudSuite access</p>
                  </div>
                </div>
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-yellow-100 rounded-lg flex items-center justify-center">
                    <GraduationCap className="w-5 h-5 text-yellow-700" />
                  </div>
                  <div>
                    <p className="font-medium text-slate-900">Certification Prep</p>
                    <p className="text-sm text-slate-600">Infor certification guidance</p>
                  </div>
                </div>
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-yellow-100 rounded-lg flex items-center justify-center">
                    <Briefcase className="w-5 h-5 text-yellow-700" />
                  </div>
                  <div>
                    <p className="font-medium text-slate-900">Job Assistance</p>
                    <p className="text-sm text-slate-600">Resume & interview prep</p>
                  </div>
                </div>
                
                {/* Demo Request Form */}
                <div className="pt-4 border-t border-slate-100">
                  <p className="text-sm font-medium text-slate-700 mb-2">Request Free Demo</p>
                  <form onSubmit={handleDemoRequest} className="flex gap-2">
                    <Input 
                      type="email" 
                      placeholder="Enter your email" 
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="flex-1"
                      data-testid="infor-demo-email"
                    />
                    <Button type="submit" className="bg-yellow-600 hover:bg-yellow-700" disabled={loading}>
                      {loading ? '...' : 'Go'}
                    </Button>
                  </form>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Industries */}
      <section className="py-12 bg-white border-y border-slate-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-center text-slate-500 mb-6 font-medium">Industries Using Infor CloudSuite</p>
          <div className="flex flex-wrap justify-center gap-4">
            {industries.map((industry) => (
              <div key={industry.name} className="flex items-center px-4 py-2 bg-slate-50 rounded-full text-sm">
                <industry.icon className="w-4 h-4 text-yellow-600 mr-2" />
                <span className="text-slate-700">{industry.name}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Infor CloudSuite Certification Paths */}
      <section className="py-20 lg:py-28" id="certifications">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <Badge className="bg-yellow-100 text-yellow-700 mb-4">Infor CloudSuite Certifications</Badge>
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4">Choose Your Infor CloudSuite Certification Path</h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Enroll in industry-specific Infor CloudSuite certification training
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 gap-8">
            {trainingPaths.map((path) => (
              <Card 
                key={path.id} 
                className={`relative overflow-hidden border-2 transition-all duration-300 cursor-pointer ${
                  selectedPath === path.id ? 'border-yellow-500 shadow-xl ring-2 ring-yellow-200' : 'border-slate-200 hover:border-yellow-300 hover:shadow-lg'
                }`}
                onClick={() => setSelectedPath(path.id)}
                data-testid={`cert-path-${path.id}`}
              >
                <div className={`h-2 bg-gradient-to-r ${path.color}`} />
                <CardHeader className="pb-2">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <span className="text-3xl">{path.icon}</span>
                      <div>
                        <CardTitle className="text-xl text-slate-900">{path.name}</CardTitle>
                        <p className="text-sm text-slate-500">{path.level} • {path.duration}</p>
                      </div>
                    </div>
                    <Badge className={`${path.demand === 'Very High' ? 'bg-green-500 text-white' : 'bg-green-100 text-green-700'}`}>
                      {path.demand} Demand
                    </Badge>
                  </div>
                  <CardDescription className="text-slate-600">{path.description}</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {/* Industries */}
                    <div className="flex flex-wrap gap-2">
                      {path.industries.map((ind, i) => (
                        <span key={i} className="text-xs px-2 py-1 bg-slate-100 text-slate-600 rounded-full">{ind}</span>
                      ))}
                    </div>
                    
                    {/* Salary */}
                    <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg">
                      <span className="text-sm text-slate-600">Expected Salary:</span>
                      <span className="font-bold text-green-700">{path.salary}</span>
                    </div>
                    
                    {/* Topics */}
                    <div>
                      <p className="text-sm font-medium text-slate-700 mb-2">Key Topics:</p>
                      <div className="grid grid-cols-2 gap-1">
                        {path.topics.slice(0, 6).map((topic, i) => (
                          <div key={i} className="flex items-center text-xs text-slate-600">
                            <CheckCircle className="w-3 h-3 text-yellow-500 mr-1 flex-shrink-0" />
                            <span className="truncate">{topic}</span>
                          </div>
                        ))}
                      </div>
                      {path.topics.length > 6 && (
                        <p className="text-xs text-yellow-600 mt-1">+{path.topics.length - 6} more topics</p>
                      )}
                    </div>
                    
                    {/* CTA */}
                    <div className="flex gap-3 pt-2">
                      <Link to="/pricing" className="flex-1">
                        <Button className={`w-full bg-gradient-to-r ${path.color} text-white hover:opacity-90`}>
                          Enroll Now
                        </Button>
                      </Link>
                      <Link to="/webinar">
                        <Button variant="outline" className="border-slate-300">
                          <Play className="w-4 h-4" />
                        </Button>
                      </Link>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Birst Analytics Add-on */}
          <div className="mt-12 p-6 bg-orange-50 rounded-2xl border border-orange-200">
            <div className="flex flex-col md:flex-row items-center justify-between gap-4">
              <div className="flex items-center gap-4">
                <span className="text-3xl">{technicalPath.icon}</span>
                <div>
                  <h3 className="font-bold text-slate-900">{technicalPath.name} <Badge variant="outline" className="ml-2 border-orange-400 text-orange-600">Add-on Track</Badge></h3>
                  <p className="text-sm text-slate-600">{technicalPath.description}</p>
                  <p className="text-sm text-slate-500 mt-1">{technicalPath.duration} • {technicalPath.salary}</p>
                </div>
              </div>
              <Link to="/contact">
                <Button variant="outline" className="border-orange-400 text-orange-600 hover:bg-orange-100">
                  Inquire About Analytics Track
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Training Curriculum */}
      <section className="py-20 lg:py-28 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4">Infor Training Curriculum</h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Comprehensive modules covering Infor CloudSuite applications
            </p>
          </div>
          
          <Tabs defaultValue="financials" className="w-full">
            <TabsList className="flex flex-wrap justify-center gap-2 mb-10 bg-transparent">
              {inforModules.slice(0, 4).map((module) => (
                <TabsTrigger 
                  key={module.id} 
                  value={module.id}
                  className="px-4 py-2 rounded-full border border-slate-200 data-[state=active]:border-yellow-500 data-[state=active]:bg-yellow-50 data-[state=active]:text-yellow-700"
                >
                  {module.name}
                </TabsTrigger>
              ))}
            </TabsList>

            {inforModules.map((module) => (
              <TabsContent key={module.id} value={module.id}>
                <Card className="max-w-4xl mx-auto">
                  <CardContent className="p-8">
                    <div className="flex items-start space-x-4 mb-6">
                      <div className="w-12 h-12 bg-yellow-100 rounded-xl flex items-center justify-center">
                        <module.icon className="w-6 h-6 text-yellow-700" />
                      </div>
                      <div>
                        <h3 className="text-2xl font-bold text-slate-900">{module.name}</h3>
                        <p className="text-yellow-600 font-medium">Duration: {module.duration}</p>
                      </div>
                    </div>
                    
                    <div className="grid md:grid-cols-2 gap-6">
                      <div>
                        <h4 className="font-semibold text-slate-900 mb-4">Topics Covered</h4>
                        <ul className="space-y-3">
                          {module.topics.map((topic, i) => (
                            <li key={i} className="flex items-start space-x-2">
                              <CheckCircle className="w-5 h-5 text-yellow-500 mt-0.5 flex-shrink-0" />
                              <span className="text-slate-600">{topic}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                      <div>
                        <h4 className="font-semibold text-slate-900 mb-4">What You'll Learn</h4>
                        <ul className="space-y-3">
                          <li className="flex items-start space-x-2">
                            <BookOpen className="w-5 h-5 text-yellow-500 mt-0.5" />
                            <span className="text-slate-600">Real-time scenario-based training</span>
                          </li>
                          <li className="flex items-start space-x-2">
                            <Monitor className="w-5 h-5 text-yellow-500 mt-0.5" />
                            <span className="text-slate-600">Hands-on lab exercises</span>
                          </li>
                          <li className="flex items-start space-x-2">
                            <FileText className="w-5 h-5 text-yellow-500 mt-0.5" />
                            <span className="text-slate-600">Study materials & documentation</span>
                          </li>
                          <li className="flex items-start space-x-2">
                            <Award className="w-5 h-5 text-yellow-500 mt-0.5" />
                            <span className="text-slate-600">Certification guidance</span>
                          </li>
                        </ul>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>
            ))}
          </Tabs>

          {/* All Modules Grid */}
          <div className="mt-12">
            <h3 className="text-xl font-bold text-slate-900 mb-6 text-center">All Infor Modules</h3>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 max-w-5xl mx-auto">
              {inforModules.map((module, index) => (
                <Card key={module.id} className="hover:shadow-md transition-shadow">
                  <CardContent className="p-4">
                    <div className="flex items-center space-x-3">
                      <div className="w-10 h-10 bg-yellow-100 rounded-lg flex items-center justify-center">
                        <span className="text-yellow-700 font-mono font-bold text-sm">{String(index + 1).padStart(2, '0')}</span>
                      </div>
                      <div>
                        <p className="font-medium text-slate-900 text-sm">{module.name}</p>
                        <p className="text-xs text-slate-500">{module.duration}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Career Outcomes */}
      <section className="py-20 lg:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12">
            <div>
              <h2 className="text-2xl font-bold text-slate-900 mb-6">Infor Certifications We Prepare You For</h2>
              <p className="text-slate-600 mb-6">
                Our training programs prepare you for official Infor certifications.
              </p>
              <ul className="space-y-3">
                {[
                  { cert: 'Infor CloudSuite Industrial (SyteLine) Certification', color: 'text-yellow-600' },
                  { cert: 'Infor CloudSuite Distribution Certification', color: 'text-green-600' },
                  { cert: 'Infor CloudSuite Financials Certification', color: 'text-blue-600' },
                  { cert: 'Infor CloudSuite HCM Certification', color: 'text-purple-600' },
                  { cert: 'Infor Birst Analytics Certification', color: 'text-orange-600' }
                ].map((item, i) => (
                  <li key={i} className="flex items-start space-x-2">
                    <Award className={`w-5 h-5 ${item.color} mt-0.5`} />
                    <span className="text-slate-700 font-medium">{item.cert}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <h2 className="text-2xl font-bold text-slate-900 mb-6">Career Opportunities</h2>
              <p className="text-slate-600 mb-6">
                Infor skills are in demand for industry-specific solutions. Here are roles you can pursue.
              </p>
              <ul className="space-y-3">
                {[
                  'Infor CloudSuite Consultant',
                  'Infor Functional Analyst',
                  'Infor Technical Consultant',
                  'Infor Implementation Specialist',
                  'Infor Solution Architect',
                  'Infor Project Manager',
                  'Infor Birst Analyst',
                  'Infor Support Specialist'
                ].map((role, i) => (
                  <li key={i} className="flex items-start space-x-2">
                    <Briefcase className="w-5 h-5 text-yellow-500 mt-0.5" />
                    <span className="text-slate-700">{role}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Corporate Clients */}
      <section className="py-16 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-center text-slate-500 mb-6 font-medium">Companies Using Infor CloudSuite</p>
          <div className="flex flex-wrap justify-center gap-6">
            {corporateClients.map((client) => (
              <div key={client} className="px-6 py-3 bg-white rounded-lg shadow-sm text-slate-700 font-medium">
                {client}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 lg:py-28 bg-gradient-to-r from-yellow-600 to-yellow-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6">
              Ready to Master Infor CloudSuite?
            </h2>
            <p className="text-lg text-yellow-100 mb-8">
              Join our next batch and become a certified Infor professional. 
              Customized training available for individuals and corporate teams.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link to="/pricing">
                <Button className="bg-white text-yellow-700 hover:bg-yellow-50 rounded-full px-8 py-3 font-medium">
                  View Pricing & Enroll
                </Button>
              </Link>
              <Link to="/contact">
                <Button variant="outline" className="border-white text-white hover:bg-white/10 rounded-full px-8 py-3 font-medium">
                  Request Corporate Training
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
